// Demo data matching the design canvas (Blaze Fire Systems Ltd). Typed
// against the generated contract, so it cannot drift from the API shapes.
import type { LedgerSource } from './source';
import { demoBank, hasBankDetails, setDesignLookup } from './mockBank';
import { currentTemplates, demoDocuments, setRateLookup } from './mockDocuments';
import { attachmentsFor, brandAssetExists, demoFiles, setTemplateLookup } from './mockFiles';
import { demoExports } from './mockExports';
import { demoSearch } from './mockSearch';
import { demoOrganisation, demoOrgContextExtras } from './mockOrganisation';
import { demoNumbering } from './mockNumbering';
import { demoVat } from './mockVat';
import { demoProducts } from './mockProducts';
import { demoFx, rateText } from './mockFx';
import { demoInbox } from './mockInbox';
import { demoJurisdiction, demoMtd } from './mockMtd';
import { demoLedger } from './mockLedger';
import { demoSettings } from './mockSettings';
import { demoAudit } from './mockAudit';

// The parts of the demo that cross stores, wired here so the stores don't import each other in circles.
setTemplateLookup(() => currentTemplates().map((t) => ({ name: t.name, logo: t.settings.brand?.logo_asset_id ?? null })));
setRateLookup(rateText);
setDesignLookup((code) => currentTemplates().some((t) => t.settings.bank_account_code === code));

const delay = <T,>(v: T, ms = 120): Promise<T> => new Promise((r) => setTimeout(() => r(v), ms));

const aged = [
  { contact_id: 'c1', contact_name: 'Swords Pavilions', current: '4200.00', d30: '0', d60: '0', d90: '0', older: '0', total: '4200.00' },
  { contact_id: 'c2', contact_name: 'Parkwest Business Campus', current: '2870.50', d30: '0', d60: '0', d90: '0', older: '0', total: '2870.50' },
  { contact_id: 'c3', contact_name: 'Ballymun Credit Union', current: '2800.00', d30: '0', d60: '0', d90: '0', older: '0', total: '2800.00' },
  { contact_id: 'c4', contact_name: 'Dublin Hotels Group', current: '0', d30: '900.00', d60: '0', d90: '0', older: '0', total: '900.00' },
  { contact_id: 'c5', contact_name: 'Sandyford Retail Ltd', current: '0', d30: '0', d60: '820.00', d90: '0', older: '0', total: '820.00' },
  { contact_id: 'c6', contact_name: 'Naas Road Logistics', current: '0', d30: '586.00', d60: '0', d90: '0', older: '0', total: '586.00' },
  { contact_id: 'c7', contact_name: 'Tallaght Medical Centre', current: '0', d30: '0', d60: '0', d90: '0', older: '310.00', total: '310.00' },
];

const DEMO_ORG = {
  id: '00000000-0000-4000-8000-000000000001', legal_name: 'Blaze Fire Systems Ltd', jurisdiction: 'IE' as const, base_currency: 'EUR',
  entity_type: 'company' as const, vat_basis: 'invoice' as const, read_only: false, vat_number: 'IE1234567T',
};

/** The demo invitation link: /invitations/accept?token=demo-invite */
export const DEMO_INVITE = 'demo-invite';

export const mock: LedgerSource = {
  ...demoDocuments,
  ...demoInbox,
  ...demoSettings,
  me: () => delay({
    subject: 'ledger:user:brian', email: 'brian@blazefire.ie', name: 'Brian',
    organisations: [{ id: DEMO_ORG.id, name: DEMO_ORG.legal_name, legal_name: DEMO_ORG.legal_name, jurisdiction: 'IE', base_currency: 'EUR', role: 'owner', read_only: false, access_ends_at: null }],
  }),
  createOrganisation: () => Promise.reject(new Error('New books can’t be set up with demo data. Connect the app to the Ledger API first.')),
  invitationPreview: (token) => token === DEMO_INVITE
    ? delay({ organisation: DEMO_ORG.legal_name, role: 'accountant', firm: 'O’Neill & Co', email_hint: 's••••••@oneillco.ie', status: 'pending', expires_at: '2026-10-25T12:00:00Z' })
    : Promise.reject(new Error('That invitation link isn’t valid. Ask for a new one.')),
  acceptInvitation: (token) => token === DEMO_INVITE ? delay(DEMO_ORG) : Promise.reject(new Error('That invitation link isn’t valid. Ask for a new one.')),
  organisation: () =>
    delay({
      // The demo can pretend to be UK books, to show Making Tax Digital (see mockMtd).
      organisation: { ...(demoJurisdiction() === 'UK'
        ? {
          id: '00000000-0000-4000-8000-000000000001', legal_name: 'Blaze Fire Systems Ltd', jurisdiction: 'UK' as const, base_currency: 'GBP',
          entity_type: 'company' as const, vat_basis: 'invoice' as const, vat_frequency: 'quarterly' as const, read_only: false, vat_number: 'GB123456789',
        }
        : {
          id: '00000000-0000-4000-8000-000000000001',
          legal_name: 'Blaze Fire Systems Ltd',
          jurisdiction: 'IE' as const,
          base_currency: 'EUR',
          entity_type: 'company' as const,
          vat_basis: 'invoice' as const,
          vat_frequency: 'bimonthly' as const,
          read_only: false,
          vat_number: 'IE1234567T',
        }), ...demoOrgContextExtras() },
      role: 'owner',
      // The owner's capabilities, exactly as the database grants them (role_capabilities).
      capabilities: ['audit.read', 'backups.create', 'backups.download', 'backups.restore', 'bank.reconcile', 'billing.manage', 'books.export',
        'contacts.write', 'documents.write', 'email.send', 'import', 'inbox.write', 'integrations.manage', 'journals.manual', 'periods.close',
        'recurring.approve', 'recurring.generate', 'reports.read', 'settings.accounts', 'templates.manage', 'users.manage', 'vat.file'],
    }),
  dashboard: () =>
    delay({
      cash: '18420.36',
      owed_to_you: '12486.50',
      overdue_to_you: '2616.00',
      you_owe: '4112.80',
      vat_due: { amount: '3926.75', due_date: '2026-11-23', period_label: 'Sep–Oct' },
      cash_flow: [
        { month: '2026-08', money_in: '21450.00', money_out: '17820.10' },
        { month: '2026-09', money_in: '19870.50', money_out: '16245.30' },
      ],
      to_do: [
        { kind: 'bank_lines', label: 'bank lines to reconcile', count: 12 },
        { kind: 'overdue', label: 'overdue invoices to chase', count: 4 },
        { kind: 'inbox', label: 'supplier bills to check', count: 4 },
        { kind: 'recurring', label: 'recurring runs to approve', count: 3 },
      ],
    }),
  agedDebtors: () => delay(aged),
  // The P&L report gives income and expenses both as positive figures.
  profitAndLoss: () =>
    delay([
      { code: '4000', name: 'Sales — goods', class: 'income', balance: '158220.00' },
      { code: '4010', name: 'Sales — services', class: 'income', balance: '56160.00' },
      { code: '5000', name: 'Materials purchased', class: 'expense', balance: '72412.40' },
      { code: '5100', name: 'Subcontractors', class: 'expense', balance: '24100.00' },
      { code: '6000', name: 'Wages and salaries', class: 'expense', balance: '48600.00' },
      { code: '7000', name: 'Rent and rates', class: 'expense', balance: '14400.00' },
      { code: '7200', name: 'Motor expenses', class: 'expense', balance: '8245.90' },
    ]),
  ...demoBank,
  ...demoLedger,
  ...demoFiles,
  ...demoFx,
  ...demoMtd,
  ...demoExports,
  ...demoOrganisation,
  ...demoNumbering,
  ...demoVat,
  ...demoProducts,
  ...demoSearch,
  ...demoAudit,
  document: async (id) => {
    const d = await demoDocuments.document(id);
    return { ...d, attachments: attachmentsFor({ documentId: id }) };
  },
  journals: async (q) => (await demoLedger.journals(q)).map((j) => ({ ...j, attachments: attachmentsFor({ journalId: j.id }) })),
  createJournal: async (input) => {
    const j = await demoLedger.createJournal(input);
    return { ...j, attachments: [] };
  },
  // As ledger.template_problems: the logo must exist and the chosen bank account must have details.
  saveTemplate: async (kind, layout, settings) => {
    const logo = settings.brand?.logo_asset_id;
    if (logo && !brandAssetExists(logo)) throw new Error('Template not saved: Logo not found');
    const code = settings.bank_account_code;
    if (code && !hasBankDetails(code)) throw new Error(`Template not saved: Bank account ${code} has no bank details to print`);
    return demoDocuments.saveTemplate(kind, layout, settings);
  },
};
